import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";
import { allWorks } from "@/lib/works";

const VALUES = [
  {
    icon: "ri-file-text-line",
    title: "剧情先行",
    description: "每部作品的简介都由剧情结构、人物关系与冲突推进三部分构成，不用悬念标题代替内容。",
  },
  {
    icon: "ri-filter-3-line",
    title: "筛选克制",
    description: "只保留类型、年份、地区、剧情标签四个维度，避免为了筛选而筛选，让选择变简单。",
  },
  {
    icon: "ri-database-2-line",
    title: "列表一致",
    description: "列表卡片与详情页使用同一份资料，标题、年份、封面与剧情简介一一对应，不做错配。",
  },
];

const USAGE = [
  "本站为影视剧情资料整理与推荐站点，不提供任何影片、剧集的在线播放或下载链接。",
  "站内所有作品名称、导演、演员、评分与剧情内容均为示范用途的虚构信息，如有雷同纯属巧合。",
  "评分、热度等数值为前端演示数据，仅用于展示排序与筛选效果，不代表任何真实评价体系。",
  "站内搜索与筛选均为浏览器前端完成，不会上传或保存你的搜索内容。",
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 痴汉电车影视剧情档案",
    description:
      "了解痴汉电车影视剧情档案站的定位、内容整理方式、联系方式与使用须知，站内作品资料均为示范内容。",
    keywords: "痴汉电车,关于我们,影视剧情档案,网站介绍",
    suffix: false,
  });

  const movieCount = allWorks.filter((work) => work.type === "movie").length;
  const tvCount = allWorks.filter((work) => work.type === "tv").length;

  return (
    <SiteLayout>
      <section className="relative w-full pt-28 md:pt-36 pb-12 md:pb-16 px-4 md:px-8 overflow-hidden border-b border-background-200/70">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=wide%20cinematic%20dark%20cinema%20hall%20interior%20with%20rows%20of%20empty%20seats%20and%20a%20glowing%20crimson%20red%20screen%2C%20volumetric%20projector%20beam%2C%20deep%20charcoal%20black%20atmosphere%2C%20film%20grain%2C%20high%20contrast%2C%20no%20text&width=1920&height=800&seq=chikan-about-hero&orientation=landscape"
            alt="影视档案站视觉背景"
            title="痴汉电车影视剧情档案"
            className="w-full h-full object-cover object-center opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/80 to-background-50/60" />
        </div>

        <div className="relative max-w-[1000px] mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-300 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>

          <div className="mt-8 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-primary-500" />
            <span className="font-label text-[11px] tracking-[0.28em] text-primary-300 uppercase">ABOUT US</span>
          </div>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-foreground-950">
            关于痴汉电车影视档案
          </h1>
          <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-700 max-w-2xl">
            我们是一群爱看剧情片、也爱把剧情讲清楚的影迷。这个站点只做三件事：收录、梳理、推荐。把同一个题材下零散的作品整理成可以按图索骥的档案，让每一次点开都更接近你想看的那一部。
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: String(allWorks.length), label: "收录作品" },
              { value: String(movieCount), label: "电影" },
              { value: String(tvCount), label: "电视剧" },
              { value: "周更", label: "档案更新" },
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-200/70 bg-background-100/70 px-4 py-4">
                <p className="font-heading text-2xl text-foreground-950">{stat.value}</p>
                <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-[1000px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {VALUES.map((value) => (
            <div key={value.title} className="rounded-lg border border-background-200/70 bg-background-100/60 p-5 md:p-6">
              <span className="w-11 h-11 rounded-md bg-primary-500/15 border border-primary-500/30 flex items-center justify-center">
                <i className={`${value.icon} text-primary-300 text-xl`} />
              </span>
              <h2 className="mt-5 font-heading text-lg text-foreground-950">{value.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-12 md:pb-16">
        <div className="max-w-[1000px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-8 lg:gap-12">
          <div>
            <div className="flex items-center gap-3">
              <span className="w-8 h-[2px] bg-accent-500" />
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">使用须知</h2>
            </div>
            <ul className="mt-5 space-y-4">
              {USAGE.map((item, index) => (
                <li key={index} className="flex gap-3">
                  <span className="font-label text-xs text-accent-400 mt-1 w-5 shrink-0">{String(index + 1).padStart(2, "0")}</span>
                  <p className="text-sm leading-relaxed text-foreground-600">{item}</p>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-primary-500" />
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">版权与内容声明</h2>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              站内文字内容为站点自行撰写的示范性剧情介绍，仅用于产品功能展示。所有海报与配图均为示意图片，不含任何真实影视作品素材。如内容是真实的、涉及权利的作品，请通过下方邮箱与我们联系，我们会在核实后处理。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              未经许可，请勿将本站内容用于商业用途。引用剧情简介时请注明来源「痴汉电车影视档案」。
            </p>
          </div>

          <aside className="rounded-lg border border-background-200/70 bg-background-100/70 p-5 md:p-6 h-fit">
            <h2 className="font-heading text-lg text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              对剧情资料有补充、发现作品信息有误，或希望推荐新的片目，欢迎通过邮件联系我们。目前仅提供邮件反馈渠道，暂不处理播放与下载相关咨询。
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <a
                href="mailto:archive@chikandensha-demo.com"
                className="flex items-center gap-3 px-4 py-3 rounded-md border border-background-200/80 hover:border-primary-500 transition-colors"
              >
                <i className="ri-mail-line text-primary-300" />
                <span className="text-sm text-foreground-800">archive@chikandensha-demo.com</span>
              </a>
              <div className="flex items-center gap-3 px-4 py-3 rounded-md border border-background-200/80">
                <i className="ri-time-line text-foreground-500" />
                <span className="text-sm text-foreground-600">反馈一般在 3 个工作日内查看</span>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-background-200/70">
              <p className="text-xs leading-relaxed text-foreground-500">
                本站无用户注册、评论与支付功能，所有交互均在前端完成，不会收集个人身份信息。
              </p>
            </div>

            <Link
              to="/category"
              className="mt-6 flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm transition-colors whitespace-nowrap"
            >
              去浏览作品档案
              <i className="ri-arrow-right-line" />
            </Link>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}